import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isDesktop: boolean;
  height?: number;
}

interface BubbleDataItem {
  id: number;
  logoText: string;
  value: string;
  size: string;
  top: string;
  left: string;
  textColor: string;
  backgroundColor: string;
}

interface BubbleProps {
  data: BubbleDataItem;
  isDesktop: boolean;
}

const Bubble: React.FC<BubbleProps> = ({ data, isDesktop }) => {
  const getSizeStyles = (size: string) => {
    const baseSize = isDesktop ? 1 : 0.8;
    switch (size) {
      case "small":
        return {
          width: 60 * baseSize,
          height: 60 * baseSize,
          fontSize: 14 * baseSize,
        };
      case "medium":
        return {
          width: 100 * baseSize,
          height: 100 * baseSize,
          fontSize: 16 * baseSize,
        };
      case "large":
        return {
          width: 150 * baseSize,
          height: 150 * baseSize,
          fontSize: 18 * baseSize,
        };
      default:
        return {
          width: 100 * baseSize,
          height: 100 * baseSize,
          fontSize: 16 * baseSize,
        };
    }
  };

  const sizeStyles = getSizeStyles(data.size);

  return (
    <div
      style={{
        position: "absolute",
        top: data.top,
        left: data.left,
        width: sizeStyles.width,
        height: sizeStyles.height,
        borderRadius: "50%",
        background: data.backgroundColor,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        padding: 10,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transition: "all 2s ease-in-out",
        transform: "translate(-50%, -50%)",
        fontSize: sizeStyles.fontSize,
        zIndex: 5,
      }}
    >
      <div
        style={{
          fontWeight: "normal",
          lineHeight: 1.2,
          color: data.textColor,
          marginBottom: data.value ? 5 : 0,
          zIndex: 10,
          position: "relative",
        }}
      >
        {data.logoText === "LinkedIn" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: isDesktop ? 18 : 14,
            }}
          >
            <span
              style={{
                fontSize: isDesktop ? 14 : 11,
                fontWeight: "bold",
                color: "white",
                backgroundColor: "#0077B5",
                padding: "2px 4px",
                borderRadius: 6,
                marginRight: 4,
              }}
            >
              in
            </span>
            LinkedIn
          </div>
        ) : (
          data.logoText
        )}
      </div>
      {/* {data.value && (
        <div style={{
          fontSize: isDesktop ? 24 : 18,
          fontWeight: 'normal',
          lineHeight: 1.2,
          color: data.textColor,
          zIndex: 10,
          position: 'relative'
        }}>
          {data.value}
        </div>
      )} */}
    </div>
  );
};

/**
 * 首页「关键引用来源识别」装饰图封装
 * 提取自 SearchIndex，对外仅暴露尺寸自适应。
 */
export const IndexKeyCitationSourcesChart: React.FC<Props> = ({
  isDesktop,
  height,
}) => {
  const { t } = useTranslation();

  // 气泡数据结构 - 调整位置让图表往中间集中
  const bubbleData = [
    {
      id: 1,
      logoText: "Wikipedia",
      value: "33%",
      size: "medium",
      top: "50%",
      left: "25%",
      textColor: "#FF6B35",
      backgroundColor: "linear-gradient(135deg, #FFF4F0 0%, #FFFFFF 100%)",
    },
    {
      id: 2,
      logoText: "Reddit",
      value: "21%",
      size: "medium",
      top: "30%",
      left: "50%",
      textColor: "#E6162D",
      backgroundColor: "linear-gradient(135deg, #FFF8F8 0%, #FFFFFF 100%)",
    },
    {
      id: 3,
      logoText: "Youtube",
      value: "27%",
      size: "medium",
      top: "55%",
      left: "65%",
      textColor: "#07C160",
      backgroundColor: "linear-gradient(135deg, #F8FFF8 0%, #FFFFFF 100%)",
    },
    {
      id: 4,
      logoText: "Quora",
      value: "",
      size: "small",
      top: "40%",
      left: "40%",
      textColor: "#0084FF",
      backgroundColor: "linear-gradient(135deg, #F8FBFF 0%, #FFFFFF 100%)",
    },
    {
      id: 5,
      logoText: "Linkedln",
      value: "",
      size: "small",
      top: "35%",
      left: "70%",
      textColor: "#8B5CF6",
      backgroundColor: "linear-gradient(135deg, #F8F4FF 0%, #FFFFFF 100%)",
    },
    {
      id: 6,
      logoText: "Forbes",
      value: "",
      size: "small",
      top: "60%",
      left: "15%",
      textColor: "#FF6B35",
      backgroundColor: "linear-gradient(135deg, #FFF4F0 0%, #FFFFFF 100%)",
    },
  ];

  const [animatedPositions, setAnimatedPositions] = useState(bubbleData);

  // 生成随机位置变化
  const generateRandomPosition = (originalData: (typeof bubbleData)[0]) => {
    const baseTop = parseInt(originalData.top);
    const baseLeft = parseInt(originalData.left);

    // 在原位置基础上随机偏移 ±16%
    const randomTop = Math.max(
      15,
      Math.min(75, baseTop + (Math.random() - 0.5) * 32)
    );
    const randomLeft = Math.max(
      15,
      Math.min(85, baseLeft + (Math.random() - 0.5) * 32)
    );

    return {
      ...originalData,
      top: `${randomTop}%`,
      left: `${randomLeft}%`,
    };
  };

  // 随机动画效果
  useEffect(() => {
    const animatePositions = () => {
      setAnimatedPositions((prev) =>
        prev.map((bubble) => generateRandomPosition(bubble))
      );
    };

    // 每3秒随机改变位置
    const interval = setInterval(animatePositions, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        height: `${height || (isDesktop ? 300 : 240)}px`,
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        padding: "10px",
      }}
    >
      {animatedPositions.map((data) => (
        <Bubble key={data.id} data={data} isDesktop={isDesktop} />
      ))}
    </div>
  );
};

export default IndexKeyCitationSourcesChart;
